import * as React from 'react';
import classify from 'src/utils/classify';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import MenuButton from './menu-buttons';
import {type OptionProps, Options, DefaultOption} from './options';

import css from './menu.css';


type MenuProps<V> = {
  options: V[];
  label?: React.ReactNode;
  isOpen?: boolean;
  resolveLabel?: (arg1: V) => string | React.ReactNode;
  resolveValue?: (arg1: V) => string;
  resolveIcon?: (arg1: V) => React.ReactNode;
  resolveDisabled?: (arg1: V, arg2: V[]) => boolean;
  onSelect: (arg1: V) => unknown;
  onChange?: (arg1: boolean) => unknown;
  trigger?: React.ReactNode;
  value?: string | string[];
  classNames?: {
    container?: string;
    options?: string;
    button?: string;
    option?: string;
    triggerContainer?: string;
    optionContainer?: string;
  };
  anchorPosition?: 'left' | 'right' | 'top';
  size?: 'default' | 'small';
  type?: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  //user is responsible for wiring things up properly if he/she
  //decided to pass the OptionComponent. This is an advance option
  //usually wont be needed. DefaultOption should suffice.
  OptionComponent?: React.ComponentType<OptionProps<V>>;
};

export const Menu = <
  V extends
    | {
        label: string;
        value: string;
      }
    | {
        label: string;
        value: string;
        icon?: React.ReactNode;
      }
    | {
        label: string;
        value: string;
        disabled?: boolean;
      }
    | {
        label: string;
        value: string;
        labelDisplay?: React.ReactNode;
      },
>({
  options,
  label,
  classNames,
  resolveLabel = (item: V) => item.label,
  resolveValue = (item: V) => item.value,
  resolveIcon = (_: V) => null,

  resolveDisabled = (item: V, _: Array<V>) =>
    // @ts-expect-error - TS2339 - Property 'disabled' does not exist on type '{ label: string; value: string; } | { label: string; value: string; icon?: ReactNode; } | { label: string; value: string; disabled?: boolean | undefined; } | { label: string; value: string; labelDisplay?: ReactNode; }'. | TS2339 - Property 'disabled' does not exist on type '{ label: string; value: string; } | { label: string; value: string; icon?: ReactNode; } | { label: string; value: string; disabled?: boolean | undefined; } | { label: string; value: string; labelDisplay?: ReactNode; }'.
    typeof item.disabled === 'boolean' ? item.disabled : false,

  size = 'default',
  type = 'secondary',
  disabled = false,

  trigger = (
    <MenuButton
      type={type}
      size={size}
      disabled={disabled}
      className={classNames?.button}
    >
      {label}
    </MenuButton>
  ),

  onSelect,
  onChange,
  value,
  anchorPosition = 'left',
  isOpen: propIsOpen,
  OptionComponent = DefaultOption,
}: MenuProps<V>) => (
  <ClickAway onChange={onChange}>
    {({isOpen, onOpen, clickAway, boundaryRef, triggerRef}) => {
      const controlledIsOpen = propIsOpen ?? isOpen;
      return (
        <div className={classify(css.container, classNames?.container)}>
          <div
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className={classNames?.triggerContainer}
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={triggerRef}
          >
            {trigger}
          </div>
          {/* hide options if button is disabled (discussed with swatantra)*/}
          {controlledIsOpen && !disabled && (
            <div
              className={classify(css.options, classNames?.optionContainer, {
                [css.left]: anchorPosition === 'left',
                [css.right]: anchorPosition === 'right',
                [css.top]: anchorPosition === 'top', //show menu on top of button
                [css.small]: size === 'small',
              })}
              ref={boundaryRef}
            >
              <Options
                options={options}
                value={value}
                resolveValue={resolveValue}
                resolveLabel={resolveLabel}
                resolveIcon={resolveIcon}
                onSelect={(option) => {
                  if (!resolveDisabled(option, options)) {
                    onSelect(option);
                    clickAway();
                  }
                }}
                classNames={{
                  options: classNames?.options,
                  option: classNames?.option,
                }}
                OptionComponent={OptionComponent}
                resolveDisabled={resolveDisabled}
              />
            </div>
          )}
        </div>
      );
    }}
  </ClickAway>
);

export default Menu;
